import { useEffect, useState } from 'react';
import omnigoSdk from '../services/sdk';

/**
 * Fetches one provider's start-list entries.
 *
 * Takes a descriptor from `startListProviders` plus the event's
 * `options.<provider>` slice, so it is agnostic to how the caller obtained the
 * event. An absent or unconfigured `options` resolves to an empty list rather
 * than an error — that is a not-set-up event, not a failure.
 *
 * `sdk.get` rejects with a string, so the rejection is captured into `error`
 * for the caller to render. `fetchPath` exists so tests can supply responses
 * without mocking the module.
 */
const useStartList = (provider, options, fetchPath = omnigoSdk.get) => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const path = provider && provider.isConfigured(options)
        ? provider.path(options)
        : null;

    useEffect(() => {
        // Guards against a late response from a previous path overwriting a
        // newer one, and against setting state after unmount.
        let current = true;

        if (!path) {
            setRecords([]);
            setError(null);
            setLoading(false);
            return () => { current = false; };
        }

        setLoading(true);
        setError(null);

        fetchPath(path)
            .then(entries => {
                if (!current) {
                    return;
                }
                setRecords((entries ?? []).filter(provider.hasRecord));
                setLoading(false);
            })
            .catch(ex => {
                if (!current) {
                    return;
                }
                console.warn(`omnigo: failed to load start list from ${path}`, ex);
                setError(ex);
                setRecords([]);
                setLoading(false);
            });

        return () => { current = false; };
    }, [path]);

    return { records, loading, error };
};

export {
    useStartList
};
